import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getConnectorInfo } from '@reducers/connector';
import { resetConnector } from '@reducers/membership';
import {
  workerType,
  workerTypeStatus,
  Alert,
  Popup,
  isWeb,
  navigateTo,
} from '../../../../../../../helpers';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';
import type { RootState } from '@store/types';
import type { ConnectorDetails } from '@reducers/connector/types';
import type React from 'react';

const DEFAULT_ELEMENTS = ['machines', 'files', 'production'];

export interface UseManageConnectorParams {
  rootStack?: SettingsRootStack;
}

export interface UseManageConnectorReturn {
  loaded: boolean;
  connectorDetails: ConnectorDetails;
  hasConnector: boolean;
  machineConnector: Record<string, string>;
  renderElements: Array<Record<string, string>> | undefined;
  defaultElements: string[];
  resetLoading: boolean;
  resetAlert: (options?: { text?: () => React.ReactNode }) => void;
  additionalData: Record<string, Record<string, unknown>>;
  connectorDetailsLoaded: boolean;
  connectorDetailsMembership: Record<string, unknown> | null;
  userCompanyName: string;
}

export const useManageConnector = ({ rootStack = 'Settings' }: UseManageConnectorParams = {}): UseManageConnectorReturn => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const connectorDetails = useSelector((state: RootState) => state.connector?.connectorDetails);
  const loaded = useSelector((state: RootState) => state.connector?.loaded ?? false);
  const featureFlags = useSelector((state: RootState) => state.user?.featureFlags ?? {});
  const connectorDetailsMembership = useSelector((state: RootState) => state.membership?.connectorDetails ?? null);
  const connectorDetailsLoaded = useSelector((state: RootState) => state.membership?.connectorDetailsLoaded ?? false);
  const userInfo = useSelector((state: RootState) => state.user?.userInfo ?? {});

  const [resetLoading, setResetLoading] = useState(false);

  const machineConnector = connectorDetails?.status?.find((c: Record<string, string>) => c.workerType === workerType.MACHINES) || {};
  const filesConnector = connectorDetails?.status?.find((c: Record<string, string>) => c.workerType === workerType.FILES) || {};
  const productionConnector = connectorDetails?.status?.find((c: Record<string, string>) => c.workerType === workerType.PRODUCTION) || {};

  useEffect(() => {
    dispatch(getConnectorInfo());
  }, []);

  useEffect(() => {
    if ([machineConnector?.status, filesConnector?.status, productionConnector?.status].includes(workerTypeStatus.Online)) {
      setResetLoading(false);
    }
  }, [machineConnector?.status, filesConnector?.status, productionConnector?.status]);

  const hasConnector = Boolean(loaded && connectorDetails?.connectorId);

  const additionalData: Record<string, Record<string, unknown>> = useMemo(() => ({
    [workerType.FILES]: {
      buttonText: 'View',
      buttonAction: () =>
        navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Connector', 'FileConnector') as never),
    },
    [workerType.MACHINES]: {
      buttonText: 'View',
      buttonAction: () =>
        navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Machines', 'Machines') as never),
    },
    [workerType.PRODUCTION]: {
      loading: !connectorDetailsLoaded,
      buttonText: connectorDetailsMembership ? 'View' : 'Setup',
      buttonAction: () => {
        if (!connectorDetailsLoaded) {
          return;
        }
        if (isWeb) {
          Popup({
            Component: require('@components/settings/v2/structure/connector/productionConnectorSetup').default,
            width: 480,
          });
        } else {
          navigateTo('ProductionConnectorSetup');
        }
      },
    },
  }), [connectorDetailsMembership, connectorDetailsLoaded, navigation, rootStack]);

  const renderElements = connectorDetails?.status?.filter((i: Record<string, string>) => {
    switch (i.workerType) {
      case workerType.MACHINES:
        return true;
      case workerType.FILES:
        return featureFlags['settings-connector-file'];
      case workerType.PRODUCTION:
        return featureFlags['settings-connector-production'];
    }
  });

  const resetAlert = useCallback((options?: { text?: () => React.ReactNode }) => {
    Alert({
      title: `${i18n.t('Reset my Connector')}?`,
      text: options?.text,
      position: 'vertical',
      actions: [
        {
          text: i18n.t('Reset my Connector'),
          style: 'danger',
          onPress: () => dispatch(resetConnector(setResetLoading)),
        },
        {
          text: i18n.t('Cancel'),
          onPress: () => {},
          style: 'primary',
        },
      ],
    });
  }, [dispatch]);

  const userCompanyName = (userInfo as Record<string, Record<string, string>>)?.companyProfile?.companyName || '';

  return {
    loaded,
    connectorDetails,
    hasConnector,
    machineConnector,
    renderElements,
    defaultElements: DEFAULT_ELEMENTS,
    resetLoading,
    resetAlert,
    additionalData,
    connectorDetailsLoaded,
    connectorDetailsMembership,
    userCompanyName,
  };
};
